import * as z from "zod";
import { formatDurationMs } from "@/lib/utils";

const ProofClaimStatus = z.enum([
  "pending",
  "running",
  "observed",
  "blocked",
  "skipped",
  "not_run",
]);
export type ProofClaimStatus = z.infer<typeof ProofClaimStatus>;

const ProofClaimReason = z.enum([
  "credentials",
  "network",
  "environment",
  "flaky",
  "other",
]);
export type ProofClaimReason = z.infer<typeof ProofClaimReason>;

const ProofEvalsRowStatus = z.enum(["passed", "failed", "skipped"]);
export type ProofEvalsRowStatus = z.infer<typeof ProofEvalsRowStatus>;

const ProofEvalsCounts = z.object({
  passed: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative().nullable().optional(),
});
export type ProofEvalsCounts = z.infer<typeof ProofEvalsCounts>;

const ProofEvalsRow = z.object({
  name: z.string().max(200),
  status: ProofEvalsRowStatus,
  base_status: ProofEvalsRowStatus.nullable().optional(),
  score: z.number().nullable().optional(),
});
export type ProofEvalsRow = z.infer<typeof ProofEvalsRow>;

const ProofEvalsResult = ProofEvalsCounts.extend({
  runs: z.number().int().positive().nullable().optional(),
  base: ProofEvalsCounts.nullable().optional(),
  rows: z.array(ProofEvalsRow).max(50).nullable().optional(),
});
export type ProofEvalsResult = z.infer<typeof ProofEvalsResult>;

const ProofVideoBookmarkAction = z.enum([
  "click",
  "hover",
  "scroll",
  "drag",
  "type",
  "key",
  "hold_key",
  "fill",
  "select",
  "check",
  "upload",
  "navigate",
  "back",
  "wait_for",
  "wait_ms",
  "pause",
]);

const ProofVideoBookmark = z.object({
  id: z.string().min(1),
  journal_seq: z.number().int().positive(),
  t_ms: z.number().int().nonnegative(),
  kind: z.enum(["input", "navigation", "wait", "other"]),
  action: ProofVideoBookmarkAction,
  label: z.string().min(1).max(120),
});
export type ProofVideoBookmark = z.infer<typeof ProofVideoBookmark>;

const KnownProofEvidence = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("image"),
    url: z.string(),
    label: z.string().nullable().optional(),
    /** Half of a before/after pair: the merge-base or the PR head. */
    side: z.enum(["base", "head"]).nullable().optional(),
    /** Framed for the Proof home card. */
    thumbnail: z.boolean().nullable().optional(),
  }),
  z.object({ kind: z.literal("link"), url: z.string(), label: z.string() }),
  z.object({
    kind: z.literal("evals"),
    url: z.string(),
    label: z.string(),
    result: ProofEvalsResult,
  }),
  z
    .object({
      kind: z.literal("video"),
      recording_id: z.uuid(),
      url: z.string(),
      label: z.string().nullable().optional(),
      poster_url: z.string(),
      gif_url: z.string().optional(),
      duration_ms: z.number().int().positive().max(2_147_483_647),
      /** Frame size in pixels, so the player opens at the recording's aspect ratio before any media loads. */
      width: z.number().int().positive().optional(),
      height: z.number().int().positive().optional(),
      bookmarks: z.array(ProofVideoBookmark).max(100),
    })
    .superRefine((video, ctx) => {
      for (let index = 0; index < video.bookmarks.length; index++) {
        const current = video.bookmarks[index];
        const previous = video.bookmarks[index - 1];
        if (current.t_ms > video.duration_ms) {
          ctx.addIssue({
            code: "custom",
            message: "Bookmark time must be inside the recording",
            path: ["bookmarks", index, "t_ms"],
          });
        }
        if (
          previous &&
          (current.t_ms < previous.t_ms ||
            (current.t_ms === previous.t_ms &&
              current.journal_seq <= previous.journal_seq))
        ) {
          ctx.addIssue({
            code: "custom",
            message: "Bookmarks must be ordered by time and journal sequence",
            path: ["bookmarks", index],
          });
        }
      }
    }),
]);

const KNOWN_EVIDENCE_KINDS: ReadonlySet<string> = new Set(
  KnownProofEvidence.options.map((option) => option.shape.kind.value),
);

// A kind this build does not know, from a newer backend, keeps the snapshot
// parsing and shows as a link to its URL; a malformed known kind still fails.
const UnknownProofEvidence = z
  .object({
    kind: z.string().refine((kind) => !KNOWN_EVIDENCE_KINDS.has(kind)),
    url: z.string(),
    label: z.string().nullable().optional(),
  })
  .transform(({ kind, url, label }) => ({
    kind: "link" as const,
    url,
    label: label || kind,
  }));

const ProofEvidence = z.union([KnownProofEvidence, UnknownProofEvidence]);
export type ProofEvidence = z.infer<typeof KnownProofEvidence>;

// head_sha is optional so reports settled before runs tracked heads still parse.
const ProofClaimAttempt = z.object({
  status: ProofClaimStatus,
  head_sha: z.string().nullable(),
  observed: z.string().nullable(),
  reason: ProofClaimReason.nullable(),
  evidence: z.array(ProofEvidence),
  started_at: z.string().nullable(),
  closed_at: z.string().nullable(),
  matches_claim: z.boolean().nullable().optional(),
  carried: z.boolean().optional(),
  claim: z.string().nullable().optional(),
});
export type ProofClaimAttempt = z.infer<typeof ProofClaimAttempt>;

const ProofClaim = z.object({
  id: z.string(),
  title: z.string(),
  claim: z.string(),
  hero: z.boolean(),
  status: ProofClaimStatus,
  observed: z.string().nullable(),
  reason: ProofClaimReason.nullable(),
  evidence: z.array(ProofEvidence),
  started_at: z.string().nullable(),
  closed_at: z.string().nullable(),
  head_sha: z.string().nullable().optional(),
  attempts: z.array(ProofClaimAttempt).optional(),
  /** Observed claims only: whether what Proof saw matched the statement. */
  matches_claim: z.boolean().nullable().optional(),
  /** Closed in a reused child's earlier run and not re-checked in this one. */
  carried: z.boolean().optional(),
  // Optional so cases planned before Proof tagged them still parse.
  surface: z.enum(["frontend", "backend"]).nullable().optional(),
  edge_case: z.boolean().optional(),
});
export type ProofClaim = z.infer<typeof ProofClaim>;

const ProofFinding = z.object({
  title: z.string(),
  severity: z.enum(["issue", "note"]),
  observed: z.string(),
  evidence: z.array(ProofEvidence),
  head_sha: z.string().nullable().optional(),
  /** Reported by a reused child's earlier run; it counts again only once this run reports it. */
  carried: z.boolean().optional(),
});
export type ProofFinding = z.infer<typeof ProofFinding>;

/** The backend's ProofRunSnapshot on the wire: live frames, `active_proof_runs`, and the settled `proof_report`. */
export const ProofRunSnapshot = z.object({
  child_session_id: z.string(),
  // A reused child holds one run per follow-up; optional for snapshots sent before runs had ids.
  run_id: z.string().optional(),
  pr_url: z.string(),
  pr_number: z.number(),
  // Optional so runs spawned before the backend sent titles still parse.
  pr_title: z.string().nullable().optional(),
  status: z.enum(["planning", "running", "finished", "aborted"]),
  aborted_reason: z.string().nullable(),
  head_sha: z.string().nullable().optional(),
  /** A pushed head the run has not checked yet. */
  pending_head_sha: z.string().nullable().optional(),
  // Optional so older settled reports without run timestamps still parse.
  started_at: z.string().nullable().optional(),
  finished_at: z.string().nullable().optional(),
  setup_started_at: z.string().nullable().optional(),
  cases: z.array(ProofClaim),
  findings: z.array(ProofFinding),
});
export type ProofRunSnapshot = z.infer<typeof ProofRunSnapshot>;

/** `ses_1:run_2`; runs sent before run ids fall back to their timestamps. */
export function proofRunKey(run: ProofRunSnapshot): string {
  const runPart = run.run_id ?? run.started_at ?? run.finished_at ?? "";
  return `${run.child_session_id}:${runPart}`;
}

export interface ProofPanelClaim {
  id: string;
  title: string;
  claim: string;
  hero: boolean;
  status: ProofClaimStatus;
  observed?: string;
  reason?: ProofClaimReason;
  /** Observed claims only; unset when the child did not say. */
  matchesClaim?: boolean;
  evidence: ProofEvidence[];
  duration?: string;
  /** Short SHA of the older head this claim closed on; it is not evidence for the run's head. */
  olderHead?: string;
  /** A hero a reused child's earlier run observed and this run never re-checked; its line says so. */
  earlierRun?: boolean;
  /** The PR has a newer head the run has not checked, so nothing is verified yet. */
  newerHeadUnchecked?: boolean;
  attempts?: ProofPanelAttempt[];
  surface?: "frontend" | "backend";
  edgeCase?: boolean;
}

export interface ProofPanelAttempt {
  status: ProofClaimStatus;
  observed?: string;
  reason?: ProofClaimReason;
  evidence: ProofEvidence[];
  duration?: string;
  olderHead?: string;
  claim?: string;
}

export type ProofPanelFinding = Omit<ProofFinding, "carried"> & {
  olderHead?: string;
};

export type ProofPanelRun =
  | {
      phase: "starting";
      cases: ProofPanelClaim[];
      findings: ProofPanelFinding[];
    }
  | {
      phase: "planning";
      cases: ProofPanelClaim[];
      findings: ProofPanelFinding[];
      /** The plan is in and the child is booting its environment. */
      settingUp?: boolean;
    }
  | {
      phase: "running";
      cases: ProofPanelClaim[];
      findings: ProofPanelFinding[];
    }
  | {
      phase: "finished";
      cases: ProofPanelClaim[];
      findings: ProofPanelFinding[];
      /** Set when the run ended before the child reported, e.g. `interrupted`. */
      abortedReason?: string;
      summary?: string;
      duration?: string;
    };

const shortSha = (sha: string) => sha.slice(0, 8);

/** `"9f8e7d6c"` when `sha` is known and is not the head the run is proving. */
function olderHead(
  sha: string | null | undefined,
  runHead: string | null | undefined,
): string | undefined {
  return sha && runHead && sha !== runHead ? shortSha(sha) : undefined;
}

/** `"3m 14s"` when both ISO timestamps parse. */
function durationBetween(
  startedAt: string | null | undefined,
  endedAt: string | null | undefined,
): string | undefined {
  const started = startedAt ? Date.parse(startedAt) : NaN;
  const ended = endedAt ? Date.parse(endedAt) : NaN;
  return Number.isFinite(started) && Number.isFinite(ended)
    ? formatDurationMs(ended - started)
    : undefined;
}

function panelClaim(
  item: ProofClaim,
  runHead?: string | null,
  pendingHead?: string | null,
): ProofPanelClaim {
  return {
    id: item.id,
    title: item.title,
    claim: item.claim,
    hero: item.hero,
    status: item.status,
    observed: item.observed ?? undefined,
    reason: item.reason ?? undefined,
    matchesClaim: item.matches_claim ?? undefined,
    evidence: item.evidence,
    duration: durationBetween(item.started_at, item.closed_at),
    olderHead:
      item.status === "pending" || item.status === "running"
        ? undefined
        : olderHead(item.head_sha, runHead),
    earlierRun: item.carried || undefined,
    newerHeadUnchecked: pendingHead ? true : undefined,
    attempts: (item.attempts ?? []).map((attempt) => ({
      status: attempt.status,
      observed: attempt.observed ?? undefined,
      reason: attempt.reason ?? undefined,
      evidence: attempt.evidence,
      duration: durationBetween(attempt.started_at, attempt.closed_at),
      olderHead: olderHead(attempt.head_sha, runHead),
      claim: attempt.claim ?? undefined,
    })),
    surface: item.surface ?? undefined,
    edgeCase: item.edge_case || undefined,
  };
}

/**
 * Shows only what this run reported: a reused child's earlier results belong
 * to that run's report. A finished run keeps a hero it never re-checked,
 * marked `earlierRun`, so the report still names its main claim.
 */
export function panelRunFromSnapshot(
  run: ProofRunSnapshot & { reply?: string | null },
): ProofPanelRun {
  const claims = run.cases
    .filter((item) => !item.carried || (run.status === "finished" && item.hero))
    .map((item) => panelClaim(item, run.head_sha, run.pending_head_sha));
  const findings = run.findings
    .filter((finding) => !finding.carried)
    .map((finding) => ({
      ...finding,
      olderHead: olderHead(finding.head_sha, run.head_sha),
    }));
  if (run.status === "finished" || run.status === "aborted") {
    return {
      phase: "finished",
      cases: claims,
      findings,
      abortedReason: run.aborted_reason ?? undefined,
      summary: run.reply?.trim() || undefined,
      duration: durationBetween(run.started_at, run.finished_at),
    };
  }
  if (run.status === "running")
    return { phase: "running", cases: claims, findings };
  return claims.length === 0
    ? { phase: "starting", cases: claims, findings }
    : {
        phase: "planning",
        cases: claims,
        findings,
        settingUp: Boolean(run.setup_started_at),
      };
}

/**
 * A child that stopped without a settled run can no longer report (its plan
 * row is gone), so its run closes out: open cases become `not_run`.
 */
export function closedOutProofRun(run: ProofPanelRun): ProofPanelRun {
  return {
    phase: "finished",
    cases: run.cases.map((item) =>
      item.status === "pending" || item.status === "running"
        ? { ...item, status: "not_run" }
        : item,
    ),
    findings: run.findings,
    abortedReason: "no_report",
  };
}
